import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, FileDown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { Field, inputCls } from './shared';
import { pollJob, downloadBlob } from '../../utils/aiJobs';

const FIELDS = [
  ['case_number', 'RIT / ROL de la causa de alimentos'], ['court', 'Tribunal (opcional)'], ['city', 'Ciudad del tribunal'],
  ['plaintiff_name', 'Demandante / alimentario', true], ['plaintiff_id', 'RUT/DNI demandante'],
  ['defendant_name', 'Alimentante deudor', true], ['defendant_id', 'RUT/DNI deudor'], ['employer', 'Empleador del deudor (si se conoce)'],
  ['lawyer', 'Abogado patrocinante'],
];

export const EnforcementDialog = ({ debtInput, open, onClose }) => {
  const { api } = useAuth();
  const [f, setF] = useState({ facts: '' });
  const [busy, setBusy] = useState(false);
  const [docId, setDocId] = useState(null);

  const download = async (id) => {
    const r = await api.get(`/family/demands/${id}/docx`, { responseType: 'blob' });
    downloadBlob(r.data, 'cumplimiento_alimentos.docx');
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/family/pension-debt/enforcement', { debt: debtInput, ...f });
      toast.info('El socio de Familia IA está redactando el escrito (1-2 minutos)…');
      const res = await pollJob(api, data.job_id);
      setDocId(res.demand_id);
      await download(res.demand_id);
      toast.success('Escrito listo. Revísalo antes de presentarlo.');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'No se pudo redactar el escrito');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="enforcement-dialog" className="max-h-[90vh] overflow-y-auto border-slate-700 bg-slate-900 text-white sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">Escrito de cumplimiento en Word</DialogTitle>
          <DialogDescription className="text-slate-400">Pide la liquidación de la deuda y todas las medidas de apremio del país, con la liquidación calculada. Lo que dejes vacío quedará como [completar].</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
          {FIELDS.map(([k, label, req]) => (
            <Field key={k} label={label}>
              <input data-testid={`enf-${k}`} required={req} value={f[k] || ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} />
            </Field>
          ))}
          <div className="sm:col-span-2">
            <Field label="Hechos adicionales (opcional)">
              <textarea data-testid="enf-facts" rows={3} value={f.facts} onChange={(e) => setF({ ...f, facts: e.target.value })} className={inputCls} placeholder="Ej: el deudor cambió de trabajo; tiene vehículo a su nombre…" />
            </Field>
          </div>
          <div className="sm:col-span-2 flex flex-wrap justify-end gap-2">
            {docId && (
              <button type="button" data-testid="enf-redownload" onClick={() => download(docId)} className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800">
                <FileDown className="h-4 w-4" /> Descargar de nuevo
              </button>
            )}
            <button data-testid="enf-submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-rose-500 px-6 py-2 text-sm font-semibold text-white hover:bg-rose-400 transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} {busy ? 'Redactando…' : 'Generar escrito en Word'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
